import { useId } from 'react'
import { InfoIcon } from '@/components/Icons'

export interface FieldTooltipProps {
  /** Stable id when another control also references this explanation. */
  id?: string
  /** Accessible name for the icon-only trigger. */
  accessibleLabel: string
  /** Explanation shown beside the field label. */
  text: string
  testId?: string
}

/**
 * Shows a compact field explanation on hover, keyboard focus, or tap.
 *
 * The trigger is deliberately larger than the icon. The previous Share with
 * hint exposed only a 14px hover target, which was difficult to reach and did
 * not give touch users a dependable way to reveal the text (BRAI-757).
 */
export default function FieldTooltip({
  id,
  accessibleLabel,
  text,
  testId,
}: FieldTooltipProps) {
  const generatedId = useId()
  const tooltipId = id ?? generatedId

  return (
    <span className="group relative -my-2 ml-0.5 inline-flex align-middle">
      <button
        type="button"
        aria-label={accessibleLabel}
        aria-describedby={tooltipId}
        onClick={(event) => event.currentTarget.focus()}
        className="inline-flex h-8 w-8 items-center justify-center rounded-full text-[var(--text-tertiary)] transition hover:bg-[var(--bg-tertiary)] hover:text-[var(--text-secondary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]/40"
      >
        <InfoIcon className="h-3.5 w-3.5" />
      </button>
      <span
        id={tooltipId}
        role="tooltip"
        data-testid={testId}
        className="invisible pointer-events-none absolute right-0 top-full z-50 mt-1 w-56 rounded-lg bg-slate-900 px-3 py-2 text-left text-xs font-medium leading-relaxed text-white opacity-0 shadow-lg transition-[opacity,visibility] group-hover:visible group-hover:pointer-events-auto group-hover:opacity-100 group-focus-within:visible group-focus-within:pointer-events-auto group-focus-within:opacity-100 dark:bg-slate-100 dark:text-slate-900"
      >
        {text}
      </span>
    </span>
  )
}
